import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import clsx from 'clsx';
import { Field } from '../forms/components/field';
import { Button } from '../typography/button/button';
import { ValidatorType, useValidation } from '@/hooks/use-validation';
import { AppContext } from '@/contexts/app-context';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { useLocalization } from '@/hooks/use-localization';
import { useGtm } from '@/hooks/use-gtm';
import { GoogleRecaptchaInfo } from '../google-recaptcha-info/google-recaptcha-info';
import { CloseIcon, ExpandedCloseIcon, SuccessIcon } from './svg';
import { useContactMeForm } from '@/hooks/use-contact-me-form';
import { useCompanyCreditCheck } from '../wp-blocks/company-credit-check/use-company-credit-check';

type ContactMeField = {
    label: string;
    placeholder: string;
    required: string[];
    validation_method: string;
    api_field?: string;
    min_value?: string | number;
    min_value_error_message?: string;
};

type ContactMeProps = {
    formTitle: string;
    formSubTitle?: string;
    formId: string;
    buttonText: string;
    submitButtonText?: string;
    thankYouText: string;
    gtm?: string;
    contactMethod?: 'email' | 'api';
    fields: ContactMeField[];
    onDismiss: () => void;
};

// Fallback mapping for forms saved before the "Connect to API field" dropdown
// was introduced. Prefer field.api_field (explicit) over this map.
const VALIDATION_METHOD_TO_API_FIELD: Record<string, string> = {
    email: 'email',
    phone: 'telephone',
    phone_email: 'telephone', // eslint-disable-line camelcase
    org_id: 'orgNumber', // eslint-disable-line camelcase
};

const mapFieldsToApiPayload = (formValues: Record<string, string>, fields: ContactMeField[], source: string) => {
    const mapped: Record<string, string | number> = {};

    for (const field of fields) {
        const value = formValues[field.label];
        if (value === undefined || value === '') continue;

        // Prefer the explicit api_field mapping set in the CMS; fall back to
        // the legacy validation_method-based mapping for older forms.
        const apiField = field.api_field || VALIDATION_METHOD_TO_API_FIELD[field.validation_method];
        if (apiField) {
            mapped[apiField] = value;
        } else {
            // Pass through unmapped fields using their CMS label as key
            mapped[field.label] = value;
        }
    }

    mapped.source = source;

    return mapped;
};

export const CONTACT_FORM_CLASSNAME = 'contact-banner-active';

export const ContactMe = ({
    formTitle,
    formSubTitle,
    formId,
    buttonText,
    submitButtonText,
    fields,
    thankYouText,
    gtm,
    contactMethod,
    onDismiss,
}: ContactMeProps) => {
    const { localize } = useLocalization();
    const { sendFormSubmitEvent } = useGtm();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { role, siteId, deviceType } = useContext(AppContext);
    const [isSending, setIsSending] = useState(false);
    const [finished, setFinished] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const { getValidatorByInputName } = useValidation();
    const { getPermalink, pageQuery } = useGlobalQueries();
    const [errorMessage, setErrorMessage] = useState('');
    const { contactMeForm, setContactMeForm } = useContactMeForm();
    const { submitContact } = useCompanyCreditCheck();
    const errorDivRef = useRef<HTMLDivElement>(null);
    const formContainerRef = useRef<HTMLDivElement>(null);
    const closingZendeskForFormRef = useRef(false);

    const getFieldErrorMessage = (validationMethod: string): string => {
        const errorKeyMap: Record<string, string> = {
            email: 'error-email-incorrect',
            phone: 'error-phone-incorrect',
            org_id: 'error-org-id-incorrect', // eslint-disable-line camelcase
            annualRevenue: 'error-annual-revenue-incorrect',
            loanAmount: 'error-loan-amount-incorrect',
        };

        const errorKey = errorKeyMap[validationMethod];
        if (errorKey) {
            const translated = localize(errorKey);
            if (translated && !translated.startsWith('No translation for')) {
                return translated;
            }
        }

        return '';
    };

    // Parse the optional admin-configured minimum for a loanAmount field.
    const getMinValue = (field: ContactMeField): number => {
        const raw = String(field.min_value ?? '').replace(/[\s.,]/g, '');
        return /^\d+$/.test(raw) ? parseInt(raw, 10) : 0;
    };

    // Resolve the validator for a field. loanAmount additionally enforces the
    // per-field minimum on top of the base positive-integer rule. Empty optional
    // values still pass (validation only runs when a value is present).
    const getFieldValidator = (field: ContactMeField) => {
        const baseValidator = getValidatorByInputName(field.validation_method as ValidatorType);

        if (field.validation_method === 'loanAmount') {
            const minAmount = getMinValue(field);
            return (value: string) => {
                if (!baseValidator(value)) {
                    return false;
                }
                if (minAmount > 0) {
                    return parseInt(value.replace(/[\s.,]/g, ''), 10) >= minAmount;
                }
                return true;
            };
        }

        return baseValidator;
    };

    // Resolve the error message for a field. A loanAmount field has two distinct
    // failures but the Field component renders a single message, so pick it based
    // on which rule the current value fails: a non-number uses the generic/translated
    // error, a valid number below the minimum uses the dedicated min message.
    const getFieldError = (field: ContactMeField): string => {
        if (field.validation_method === 'loanAmount') {
            const value = formValues[field.label] || '';
            const isValidNumber = getValidatorByInputName('loanAmount')(value);
            if (isValidNumber && field.min_value_error_message) {
                return field.min_value_error_message;
            }
            return getFieldErrorMessage('loanAmount');
        }
        return getFieldErrorMessage(field.validation_method);
    };

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (errorDivRef.current && !errorDivRef.current.contains(event.target as Node)) {
                // Click occurred outside the error message div
                // You can run your function here
                setErrorMessage('');
            }
        }

        document.addEventListener('click', handleClickOutside);

        if (!document.body.classList.contains(CONTACT_FORM_CLASSNAME)) {
            document.body.classList.add(CONTACT_FORM_CLASSNAME);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.body.classList.remove(CONTACT_FORM_CLASSNAME);
        };
    }, []);

    const initialFormValues = fields.reduce<Record<string, string>>((acc, field) => {
        acc[field.label] = '';
        return acc;
    }, {});

    const [formValues, setFormValues] = useState(initialFormValues);

    const handleInputChange = (name: string, value: string) => {
        setFormValues(prevValues => ({ ...prevValues, [name]: value }));
    };

    const isSubmitDisabled = useMemo(() => {
        if (isSending) {
            return true;
        }

        for (const [key, value] of Object.entries(formValues)) {
            const formField = fields.find(({ label }) => label === key);
            if (!formField || !formField?.validation_method) {
                continue;
            }

            if (formField?.required?.includes('yes') && !value) {
                return true;
            }

            const validation = getFieldValidator(formField);
            if (value && validation && !validation(value)) {
                return true;
            }
        }

        return false;
    }, [formValues, isSending]);

    const prepareFormData = (): FormData => {
        const formData = new FormData();
        Object.entries(formValues).forEach(([key, value]) => formData.append(key, value));
        formData.set('permalink', getPermalink());
        formData.set('formId', formId);
        return formData;
    };

    const submitViaEmail = async (token: string) => {
        return fetch('/api/contact-me-form', {
            headers: {
                Accept: 'application/json',
                credentials: 'include',
                'x-role': role,
                'x-site-id': siteId,
                'x-device-type': deviceType,
                'x-recaptcha-token': JSON.stringify(token),
            },
            method: 'POST',
            body: prepareFormData(),
        });
    };

    const submitViaApi = async (token: string) => {
        const pageTitle = pageQuery.data?.page_title || pageQuery.data?.post_title || '';
        const source = `${pageTitle}-${formTitle}`;
        const payload = mapFieldsToApiPayload(formValues, fields, source);
        const orgNumber = String(payload.orgNumber ?? '').replace(/[^0-9]/g, '');

        if (!orgNumber) {
            throw new Error('Missing orgNumber');
        }

        // orgNumber goes in the URL path, not the body
        delete payload.orgNumber;

        // loanAmount must be an integer per the API spec
        if (payload.loanAmount) {
            payload.loanAmount = parseInt(String(payload.loanAmount), 10) || 0;
        }

        const contactData = payload as {
            name: string;
            email: string;
            telephone: string;
            loanAmount: number;
            source?: string;
            message?: string;
            annualRevenue?: string;
        };
        return submitContact(orgNumber, contactData, token);
    };

    const handleSubmit = async () => {
        setIsFormSubmitted(true);
        setIsSending(true);

        if (!executeRecaptcha) {
            setErrorMessage(localize('invalid-captcha'));
            setIsSending(false);
            return;
        }

        const token = await executeRecaptcha();
        if (!token) {
            setErrorMessage(localize('invalid-captcha'));
            setIsSending(false);
            return;
        }

        try {
            if (contactMethod === 'api') {
                const result = await submitViaApi(token);
                setIsSending(false);

                if (result === 'OK') {
                    if (gtm) {
                        sendFormSubmitEvent(gtm, formId);
                    }
                    setFinished(true);
                } else {
                    setErrorMessage(localize('error-generic'));
                }
            } else {
                const response = await submitViaEmail(token);
                setIsSending(false);

                if (!response.ok) {
                    const data = await response.json();
                    if (response.status === 498) {
                        if (data?.error === 'INVALID_CAPTCHA_TOKEN') {
                            setErrorMessage(localize('invalid-captcha'));
                        } else {
                            setErrorMessage(localize('invalid-token'));
                        }
                    } else {
                        setErrorMessage(localize('error-generic'));
                    }
                } else {
                    if (gtm) {
                        sendFormSubmitEvent(gtm, formId);
                    }
                    setFinished(true);
                }
            }
        } catch {
            setIsSending(false);
            setErrorMessage(localize('error-generic'));
        }
    };

    const toggleOpen = () => {
        setContactMeForm({ ...contactMeForm, isOpen: !contactMeForm.isOpen });
    };

    function getGroupedValue(index: number) {
        if (index === 0) {
            return fields.length > 1 ? 'top' : '';
        }

        return index + 1 === fields.length ? 'bottom' : 'middle';
    }

    const [hasCookieButton, setHasCookieButton] = useState(false);
    const [hasZendeskChatWindow, setHasZendeskChatWindow] = useState(false);

    const handleContactFormToggle = () => {
        // Close Zendesk chat if open
        if (hasZendeskChatWindow && window.zE) {
            closingZendeskForFormRef.current = true;
            window.zE('messenger', 'close');
        }
        setContactMeForm({ isOpen: true });
    };

    useEffect(() => {
        const checkElements = () => {
            setHasCookieButton(!!document.getElementById('ot-sdk-btn-floating'));
        };

        checkElements();

        // Check again after a delay since these elements load asynchronously
        const timeout = setTimeout(checkElements, 1000);
        return () => clearTimeout(timeout);
    }, []);

    useEffect(() => {
        const isMobile = () => window.innerWidth < 820;
        const GAP = 32; // gap between form and launcher in px

        const positionElements = () => {
            // Only apply custom positioning on small mobile - let CSS handle larger screens
            if (!isMobile()) {
                return;
            }

            const launcher = document.getElementById('launcher');
            const chatWindow = document.querySelector<HTMLIFrameElement>('iframe[name="Meddelandefönster"]');
            const formHeight = formContainerRef.current?.offsetHeight || 0;

            // Hide launcher when contact form is open
            if (launcher) {
                if (contactMeForm.isOpen) {
                    launcher.style.setProperty('display', 'none', 'important');
                } else {
                    launcher.style.removeProperty('display');
                }
            }

            if (hasZendeskChatWindow) {
                // Chat is open - reset launcher, position chat window above form
                if (launcher) {
                    launcher.style.setProperty('bottom', '1rem', 'important');
                }
                if (chatWindow) {
                    chatWindow.style.setProperty('inset', `auto 1rem ${96}px auto`, 'important');
                }
            } else {
                // Chat is closed - position launcher above form
                if (launcher) {
                    const launcherBottom = formHeight + GAP;
                    launcher.style.setProperty('bottom', `${launcherBottom}px`, 'important');
                }
            }
        };

        // Position immediately
        positionElements();

        // Watch for form size changes
        const resizeObserver = new ResizeObserver(positionElements);
        if (formContainerRef.current) {
            resizeObserver.observe(formContainerRef.current);
        }

        // Also watch for the launcher appearing in the DOM (loads async)
        const mutationObserver = new MutationObserver(positionElements);
        mutationObserver.observe(document.body, { childList: true, subtree: true });

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            const launcher = document.getElementById('launcher');
            if (launcher) {
                // Remove inline styles so CSS can take over
                launcher.style.removeProperty('bottom');
                launcher.style.removeProperty('display');
            }
        };
    }, [hasZendeskChatWindow, contactMeForm.isOpen]);

    useEffect(() => {
        // Use Zendesk's API to detect when chat opens/closes
        // Note: Zendesk doesn't support messenger:off, so we don't clean up listeners
        const setupZendeskListeners = () => {
            if (window.zE) {
                window.zE('messenger:on', 'open', () => setHasZendeskChatWindow(true));
                window.zE('messenger:on', 'close', () => setHasZendeskChatWindow(false));
                return true;
            }
            return false;
        };

        if (typeof window === 'undefined') {
            return;
        }

        // Try immediately, then retry if zE isn't loaded yet
        if (!setupZendeskListeners()) {
            const interval = setInterval(() => {
                if (setupZendeskListeners()) {
                    clearInterval(interval);
                }
            }, 500);
            return () => clearInterval(interval);
        }
    }, []);

    useEffect(() => {
        // Close contact form when Zendesk opens (unless we're the ones who closed Zendesk)
        if (hasZendeskChatWindow && contactMeForm.isOpen && !closingZendeskForFormRef.current) {
            setContactMeForm({ ...contactMeForm, isOpen: false });
        }
        // Reset the ref when Zendesk confirms closed
        if (!hasZendeskChatWindow) {
            closingZendeskForFormRef.current = false;
        }
    }, [hasZendeskChatWindow, contactMeForm.isOpen]);

    return (
        <div
            ref={formContainerRef}
            data-component="contact-me"
            className={clsx(
                'r-fixed r-bottom-4 r-right-4 r-z-[1001] r-flex r-max-h-[calc(100vh-4rem)] r-flex-col r-items-center r-justify-between max-lg:r-max-w-[720px] sm:r-translate-x-0 sm:r-translate-y-0',
                'r-rounded-2xl r-bg-white/95 r-px-1 r-py-4 r-text-black r-shadow-[0_2px_1rem_0_rgba(0,0,0,.15)]',
                'md:r-bottom-8 md:r-left-auto md:r-min-w-[528px]',
                {
                    'max-sm:r-left-4': !hasCookieButton || contactMeForm.isOpen,
                    'max-sm:r-left-16 md:r-right-24': hasCookieButton,
                    'max-md:r-hidden': hasZendeskChatWindow,
                }
            )}
        >
            <button
                aria-label="Close"
                onClick={() => onDismiss()}
                className={clsx('r-absolute -r-left-2 -r-top-2 r-rounded-full r-bg-white/95', {
                    'r-hidden': contactMeForm.isOpen,
                })}
            >
                <CloseIcon className="r-size-8" />
            </button>
            <div
                className={clsx('r-container r-px-2 sm:r-px-6', {
                    'scrollbar-hide r-overflow-visible r-overflow-y-scroll': contactMeForm.isOpen,
                })}
            >
                {finished ? (
                    <div className="r-flex r-items-center r-space-x-4" role="status">
                        <SuccessIcon />
                        <p className="r-text-md r-font-bold md:r-text-lg">{thankYouText}</p>
                        <button aria-label="Close" onClick={() => onDismiss()} className="r-ml-auto r-shrink-0 r-rounded-full r-bg-white/95">
                            <CloseIcon className="r-size-8" />
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="r-flex r-w-full r-items-center r-justify-between r-gap-4">
                            {contactMeForm.isOpen && (
                                <button
                                    aria-label="Close"
                                    className="r-absolute r--top-8 r-right-8 r-cursor-pointer r-border-none r-bg-transparent r-p-0"
                                    onClick={toggleOpen}
                                >
                                    <ExpandedCloseIcon className="r-w-12 md:r-w-16" />
                                </button>
                            )}
                            <div className={clsx({ 'r-pt-6': contactMeForm.isOpen })}>
                                <p className="r-text-l r-text-balance r-font-bold md:r-text-xl">{formTitle}</p>
                                {formSubTitle && contactMeForm.isOpen ? <p className="r-py-2 r-text-sm">{formSubTitle}</p> : null}
                            </div>
                            <div className="r-justify-self-end">
                                {!contactMeForm.isOpen && (
                                    <Button type="primary" size="small" onClick={handleContactFormToggle} className="sm:r-min-w-[max-content]">
                                        {buttonText}
                                    </Button>
                                )}
                            </div>
                        </div>
                        {contactMeForm.isOpen && !finished && (
                            <form onSubmit={e => e.preventDefault()}>
                                <div className="r-flex r-flex-col">
                                    <div className="r-my-5 r-w-full">
                                        {fields.map((field, index) => (
                                            <Field
                                                grouped={getGroupedValue(index)}
                                                validation={getFieldValidator(field)}
                                                inputClassName="r-text-black"
                                                showRequired={field.required.includes('yes')}
                                                required={field.required.includes('yes')}
                                                label={field.label}
                                                value={formValues[field.label]}
                                                onChange={value => handleInputChange(field.label, value)}
                                                errorMessage={getFieldError(field)}
                                                showRequiredError={isFormSubmitted && field.required.includes('yes') && !formValues[field.label]}
                                                key={index}
                                            />
                                        ))}
                                    </div>
                                    {errorMessage ? (
                                        <div ref={errorDivRef} role="alert">
                                            <p className="r-mb-1 r-text-sm r-text-red-500">{errorMessage}</p>
                                        </div>
                                    ) : null}
                                    <div className={clsx('r-flex r-justify-between r-gap-10', { 'max-sm:r-pb-10': hasCookieButton })}>
                                        <GoogleRecaptchaInfo />
                                        <div className="r-flex r-items-end">
                                            <Button
                                                onClick={() => !isSubmitDisabled && handleSubmit()}
                                                ariaDisabled={isSubmitDisabled}
                                                type="primary"
                                                className="r-min-w-[max-content]"
                                            >
                                                {submitButtonText || localize('submit')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
